import { useLazyQuery, useQuery } from "@apollo/client";
import { composePaths, createAjv } from "@jsonforms/core";
import { useJsonForms, withJsonFormsArrayControlProps } from "@jsonforms/react";
import { debounce } from "lodash";
import { LoadingSpinner, Modal } from "pepsico-ds";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listSegmentsPaginated,
  loadSegmentSummary,
} from "../../../graphql/queries/listSegments";
import ButtonGroupDynamic from "../../common/ButtonGroupDynamic";
import JsonFormsWrapper from "../jsonFormsWrapper/JsonFormsWrapper";
import SegmentSelect from "../segmentSelect/SegmentSelect";
import "./multistep-segment-select.scss";

const MultiStepSegmentSelectComponent = ({
  visible,
  label,
  renderers,
  schema,
  ...props
}) => {
  const {
    openModal,
    selectedSegments,
    savedSegments,
    currentSegmentIndex,
    page,
    segments,
    contentImage,
    handleToggleModal,
    handleSegmentSelection,
    handleStepForward,
    handleCloseModal,
    handleImageChange,
    handleEditSegment,
    handleDeleteSegment,
    currentPageSegmentName,
    childUiSchema,
    childPath,
    isLastPage,
    isDataValid,
    setPage,
    currentPage,
    totalPages,
    handleSearch,
    handlePageChange,
    loading,
  } = useMultiStepSegmentSelect({ schema, ...props });

  const secondaryButtonProps = useMemo(
    () => ({
      text: isDataValid ? "Back to Segment" : "Previous",
      variant: "secondary",
      iconLeading: "arrow_back",
      onClick: () => setPage(page - 1),
    }),
    [isDataValid, page, setPage]
  );

  const primaryButtonProps = useMemo(
    () => ({
      text: isLastPage ? "Save" : "Next",
      ...(isLastPage ? {} : { iconTrailing: "arrow_forward" }),
      disabled: page === 0 && !selectedSegments.length,
      onClick: handleStepForward,
    }),
    [isLastPage, page, selectedSegments.length, handleStepForward]
  );

  return (
    visible && (
      <>
        <span className="multistep-segment-select-label">{label}</span>
        <div className="multistep-segment-select">
          {loading && (
            <div>
              <LoadingSpinner style={{ height: "24px", width: "24px" }} />
            </div>
          )}
          {!loading &&
            savedSegments.map((segment) => (
              <SegmentItem
                key={segment.id}
                segment={segment}
                onEdit={handleEditSegment}
                onDelete={handleDeleteSegment}
              />
            ))}
          {renderAddSegmentButton(handleToggleModal)}
          <Modal
            className={
              page > 0
                ? "segment-select-steps-modal"
                : "campaign-segment-select-modal"
            }
            showModal={openModal}
            title={
              page === 0
                ? "Choose Segment"
                : `Upload Home Page Image for ${currentPageSegmentName}`
            }
            onCloseModal={handleCloseModal}
            showTertiaryButton={false}
            showLink={false}
            showSecondaryButton={
              isLastPage && currentSegmentIndex === savedSegments.length
            }
            secondaryButtonProps={secondaryButtonProps}
            primaryButtonProps={primaryButtonProps}
          >
            {page === 0 ? (
              <SegmentSelect
                segmentsData={segments}
                selected={selectedSegments}
                onChange={handleSegmentSelection}
                selection="single"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onSearch={handleSearch}
              />
            ) : (
              <div className="segment-steps-content-wrapper">
                <JsonFormsWrapper
                  path={childPath}
                  renderers={renderers}
                  schema={schema}
                  uischema={childUiSchema}
                  onChange={handleImageChange}
                  data={contentImage}
                />
              </div>
            )}
          </Modal>
        </div>
      </>
    )
  );
};

const SEGMENTS_PATH = "segments";
const useMultiStepSegmentSelect = ({
  data,
  removeItems,
  handleChange,
  path,
  schema,
  uischema,
  addItem,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedSegments, setSavedSegments] = useState([]);
  const [contentImage, setContentImage] = useState({});
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;
  const filterRef = useRef({ keyword: "", status: "ENABLED" });
  const ctx = useJsonForms();
  const campaignSegments = useMemo(
    () => ctx?.core?.data?.segments || [],
    [ctx?.core?.data?.segments]
  );
  const contentSegments = useMemo(
    () => ctx?.core?.data?.segmentedContents || [],
    [ctx?.core?.data?.segmentedContents]
  );
  const [loadSegmentSummaryCall] = useLazyQuery(loadSegmentSummary, {
    fetchPolicy: "cache-first",
  });

  const { data: segmentsData, refetch } = useQuery(listSegmentsPaginated, {
    variables: {
      filter: {
        status: "ENABLED",
        keyword: undefined,
      },
      limit,
      offset: 0,
    },
    fetchPolicy: "network-only",
  });

  const listSegmentsRes = segmentsData?.listSegmentsPaginated?.items;
  const totalCount = segmentsData?.listSegmentsPaginated?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    if (!campaignSegments.length) {
      setSavedSegments([]);
      return;
    }
    setLoading(true);
    Promise.all(
      campaignSegments.map((id) =>
        loadSegmentSummaryCall({
          variables: { id },
          fetchPolicy: "cache-first",
        })
      )
    )
      .then((results) => {
        const savedData = results.map((result, index) => {
          const segment = result?.data?.loadSegment;
          const id = campaignSegments[index];
          const contentData = contentSegments?.find(
            (contentSegment) =>
              contentSegment.id === id || contentSegment?.segments?.[0] === id
          )?.contentData;

          if (segment?.status === "ENABLED") {
            return { ...segment, contentData };
          } else {
            return {
              ...segment,
              name: "Selected segment is no longer active",
              status: "DISABLED",
              contentData,
            };
          }
        });
        setSavedSegments(savedData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [campaignSegments, contentSegments, loadSegmentSummaryCall]);

  const segments = useMemo(
    () => getAvailableSegments(listSegmentsRes, savedSegments),
    [listSegmentsRes, savedSegments]
  );

  const addItemCallback = useCallback(
    (path, value) => addItem(path, value),
    [addItem]
  );

  const handleImageChange = useCallback(
    (data) => {
      if (data?.contentData?.campaign_image_url) {
        setContentImage(data);
      } else {
        setContentImage({});
      }
    },
    [setContentImage]
  );

  const removeItemsCallback = useCallback(
    (path, toDelete) => removeItems(path, toDelete),
    [removeItems]
  );

  const onChangeFilters = useMemo(
    () =>
      debounce(() => {
        setCurrentPage(1);
        refetch({
          filter: {
            status: filterRef.current.status,
            keyword: filterRef.current.keyword || undefined,
          },
          limit,
          offset: 0,
        });
      }, 500),
    [refetch, limit]
  );

  const handleSearch = useCallback(
    (value) => {
      filterRef.current.keyword = value;
      onChangeFilters();
    },
    [onChangeFilters]
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      refetch({
        filter: {
          status: filterRef.current.status,
          keyword: filterRef.current.keyword || undefined,
        },
        limit,
        offset: (page - 1) * limit,
      });
    },
    [refetch, limit]
  );

  const handleToggleModal = useCallback(() => {
    setOpenModal((prev) => !prev);
  }, []);

  const handleSegmentSelection = useCallback(
    (selected) => {
      setSelectedSegments(selected);
    },
    [setSelectedSegments]
  );

  const handleRemoveSaved = useCallback(
    (id) => {
      const contentSegmentsWithoutRemoved = contentSegments.filter(
        (segment) => segment?.segments?.length > 0 && segment.id !== id
      );
      const campaignSegmentsWithoutRemoved = campaignSegments.filter(
        (segment) => segment !== id
      );

      handleChange(path, contentSegmentsWithoutRemoved);
      handleChange(SEGMENTS_PATH, campaignSegmentsWithoutRemoved);
    },
    [contentSegments, campaignSegments, handleChange, path]
  );

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setPage(0);
    setSelectedSegments([]);
    setContentImage({});
  }, [setOpenModal, setPage, setSelectedSegments, setContentImage]);

  const handleStepForward = useCallback(() => {
    if (page === 0) {
      if (data?.length - savedSegments.length > 0) {
        removeItemsCallback(path, [data.length - 1])();
      }

      setPage(page + 1);
      setCurrentSegmentIndex(savedSegments.length);
      return;
    }

    const toAddSegments = selectedSegments.filter(
      (segment) => !campaignSegments?.some((item) => item === segment?.id)
    );

    if (toAddSegments?.length > 0) {
      handleChange(SEGMENTS_PATH, [...campaignSegments, toAddSegments[0].id]);
    }

    if (selectedSegments?.[0]?.id && contentImage?.contentData?.campaign_image_url) {
      const currentId = selectedSegments?.[0]?.id;

      const isModifiedContent = contentSegments.some((segment) =>
        segment?.segments?.includes(currentId)
      );

      if (isModifiedContent) {
        const modifiedContentSegments = contentSegments.map((segment) => {
          if (segment?.segments?.includes(currentId)) {
            return {
              ...segment,
              ...contentImage,
              segments: [currentId],
            };
          }
          return segment;
        });

        handleChange(path, modifiedContentSegments);
      } else {
        addItemCallback(path, {
          ...selectedSegments[0],
          ...contentImage,
          segments: [currentId],
        })();
      }
    }

    handleCloseModal();
  }, [
    page,
    savedSegments,
    data,
    removeItemsCallback,
    path,
    selectedSegments,
    campaignSegments,
    handleChange,
    contentImage,
    contentSegments,
    addItemCallback,
    handleCloseModal,
  ]);

  const handleEditSegment = useCallback(
    (segment) => {
      if (segment.contentData?.campaign_image_url) {
        setContentImage({ contentData: segment.contentData });
      }
      setSelectedSegments([segment]);
      setPage(1);
      handleToggleModal();
    },
    [setContentImage, setSelectedSegments, setPage, handleToggleModal]
  );

  const handleDeleteSegment = useCallback(
    (id) => {
      handleRemoveSaved(id);
    },
    [handleRemoveSaved]
  );

  const isLastPage = page === 1;
  const currentPageSegmentName = listSegmentsRes?.find(
    (segment) => segment.id === selectedSegments[0]?.id
  )?.name;
  const childUiSchema = uischema.options?.detail;
  const childPath = composePaths(path, `${currentSegmentIndex}`);

  const ajv = useMemo(() => createAjv(), []);
  const isDataValid = ajv.validate(schema, data?.[currentSegmentIndex] || {});

  return {
    openModal,
    selectedSegments,
    savedSegments,
    currentSegmentIndex,
    page,
    segments,
    contentImage,
    handleToggleModal,
    handleSegmentSelection,
    handleStepForward,
    handleCloseModal,
    handleImageChange,
    handleEditSegment,
    handleDeleteSegment,
    currentPageSegmentName,
    childUiSchema,
    childPath,
    isLastPage,
    isDataValid,
    setSelectedSegments,
    setPage,
    setContentImage,
    currentPage,
    totalPages,
    handleSearch,
    handlePageChange,
    loading,
  };
};

// MARK :- Helper Functions

const getAvailableSegments = (listSegmentsRes, savedSegments) => {
  return (
    listSegmentsRes?.filter(
      (segment) => !savedSegments?.some((item) => item.id === segment.id)
    ) || []
  );
};

const SegmentItem = memo(({ segment, onEdit, onDelete }) => {
  const handleEdit = useCallback(() => onEdit(segment), [onEdit, segment]);
  const handleDelete = useCallback(
    () => onDelete(segment.id),
    [onDelete, segment.id]
  );

  return (
    <div
      className={`segment-item display-flex flex-direction-row font-xs font-bold segment-added ${segment.status === "DISABLED" ? "segment-disabled" : ""}`}
    >
      <div className="added-segment display-flex flex-direction-row align-items-center justify-content-space-between">
        <div className="segment-content display-flex flex-direction-row align-items-center">
          {segment.contentData?.campaign_image_url && (
            <img
              className="segment-homepage-image"
              src={segment.contentData.campaign_image_url}
              alt={`Homepage for ${segment.name}`}
            />
          )}
          <span>{segment.name}</span>
        </div>
        <ButtonGroupDynamic
          size="small"
          items={[
            { icon: "edit", onClick: handleEdit },
            { icon: "delete", onClick: handleDelete },
          ]}
        />
      </div>
    </div>
  );
});
SegmentItem.displayName = "SegmentItem";

const renderAddSegmentButton = (onClick) => (
  <div className="segment-item display-flex flex-direction-row font-xs font-bold">
    <ButtonGroupDynamic
      className="add-new"
      size="small"
      items={[{ icon: "add", onClick }]}
    />
    Add New Segment
  </div>
);

export const MultiStepSegmentSelectControl = withJsonFormsArrayControlProps(
  MultiStepSegmentSelectComponent
);
